import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { StarRatingInput } from './StarRatingInput';
import { parseReviewOrderId } from '../utils/reviewOrderLink';

interface ReviewFormProps {
  /** Mörk bakgrund (t.ex. /recension) – anpassad styling */
  variant?: 'light' | 'dark';
  /** Order-id från /recension?order=… – sparas på recensionen */
  rentalRequestId?: string | null;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  variant = 'light',
  rentalRequestId = null,
}) => {
  const [formData, setFormData] = useState({
    customerName: '',
    rating: 5,
    comment: '',
    eventType: '',
  });

  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isDark = variant === 'dark';
  const linkedOrderId = parseReviewOrderId(rentalRequestId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.rating < 1 || formData.rating > 5) {
      setError('Välj ett betyg mellan 1 och 5 stjärnor');
      return;
    }

    setSubmitting(true);
    try {
      const { error: submitError } = await supabase.from('reviews').insert([
        {
          customer_name: formData.customerName.trim(),
          rating: formData.rating,
          comment: formData.comment.trim(),
          event_type: formData.eventType,
          ...(linkedOrderId ? { rental_request_id: linkedOrderId } : {}),
        },
      ]);

      if (submitError) throw submitError;

      setSubmitted(true);
      setFormData({
        customerName: '',
        rating: 5,
        comment: '',
        eventType: '',
      });
    } catch (err: unknown) {
      console.error('Error submitting review:', err);
      setError('Ett fel uppstod när recensionen skulle skickas');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className={
          isDark
            ? 'rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-6 text-center'
            : 'rounded-none bg-green-50 p-6 text-center'
        }
      >
        <h3
          className={`text-xl font-bold mb-2 ${isDark ? 'text-emerald-300' : 'text-green-800'}`}
        >
          Tack för din recension!
        </h3>
        <p className={isDark ? 'text-emerald-200/90' : 'text-green-700'}>
          Din recension kommer att publiceras efter granskning.
        </p>
      </div>
    );
  }

  const labelClass = isDark
    ? 'block text-sm font-medium text-slate-300 mb-1'
    : 'block text-sm font-medium text-gray-700 mb-1';
  const fieldClass = isDark
    ? 'w-full p-2.5 border border-slate-600 rounded-lg bg-slate-900/60 text-white placeholder-slate-500 focus:outline-none focus:border-purple-500'
    : 'w-full p-2 border rounded-none bg-white text-gray-900';

  return (
    <div
      className={
        isDark
          ? 'rounded-lg border border-slate-700 bg-slate-900/50 p-6'
          : 'bg-white p-6 rounded-none shadow-md'
      }
    >
      <h3 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Lämna en recension
      </h3>

      {error && (
        <div
          className={
            isDark
              ? 'bg-red-500/15 border border-red-500/40 text-red-200 px-4 py-3 rounded-lg mb-4'
              : 'bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4'
          }
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className={labelClass} htmlFor="review-customer-name">
            Ditt namn
          </label>
          <input
            id="review-customer-name"
            type="text"
            value={formData.customerName}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, customerName: e.target.value }))
            }
            className={fieldClass}
            required
          />
        </div>

        <div className="mb-4">
          <label className={labelClass} htmlFor="review-event-type">
            Typ av evenemang
          </label>
          <select
            id="review-event-type"
            value={formData.eventType}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, eventType: e.target.value }))
            }
            className={fieldClass}
            required
          >
            <option value="">Välj typ av evenemang</option>
            <option value="Bröllop">Bröllop</option>
            <option value="Födelsedagsfest">Födelsedagsfest</option>
            <option value="Företagsevent">Företagsevent</option>
            <option value="Studentfest">Studentfest</option>
            <option value="Student">Student</option>
            <option value="DJ">DJ</option>
            <option value="Fest">Fest</option>
            <option value="Karaoke">Karaoke</option>
            <option value="Annat">Annat</option>
          </select>
        </div>

        <div className="mb-4">
          <span className={labelClass}>Betyg</span>
          <StarRatingInput
            value={formData.rating}
            onChange={(rating) => setFormData((prev) => ({ ...prev, rating }))}
          />
        </div>

        <div className="mb-6">
          <label className={labelClass} htmlFor="review-comment">
            Din recension
          </label>
          <textarea
            id="review-comment"
            value={formData.comment}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, comment: e.target.value }))
            }
            rows={4}
            className={fieldClass}
            required
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-medium py-2.5 px-4 rounded-lg transition-colors duration-300"
        >
          {submitting ? 'Skickar…' : 'Skicka recension'}
        </button>
      </form>
    </div>
  );
};

export default ReviewForm;
